import { useEffect, useRef, useState } from 'react';
import { MapPin, X } from 'lucide-react';
import { publicApi } from './fairApi';
import { SearchSelect, inputCls } from './FormBits';

const useStates = () => {
  const [states, setStates] = useState([]);
  useEffect(() => { publicApi.get('/location-master/states').then((r) => setStates(r.data || [])).catch(() => {}); }, []);
  return states;
};

const uniq = (arr) => [...new Set(arr.filter(Boolean))].sort();

export const LocationPicker = ({ value = {}, onChange, withDistrict = true, testId }) => {
  const states = useStates();
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    if (!value.state) return setDistricts([]);
    publicApi.get('/location-master/districts', { params: { state: value.state } }).then((r) => setDistricts(r.data || [])).catch(() => {});
    if (!withDistrict) {
      publicApi.get('/location-master/cities', { params: { state: value.state } })
        .then((r) => setCities(uniq((r.data || []).map((c) => c.city)))).catch(() => {});
    }
  }, [value.state, withDistrict]);

  useEffect(() => {
    if (!withDistrict) return;
    if (!value.district) return setCities([]);
    const d = districts.find((x) => x.district === value.district);
    publicApi.get('/location-master/cities', { params: { state: value.state, district: value.district } })
      .then((r) => setCities(uniq([...(r.data || []).map((c) => c.city), ...(d?.cities || []), value.district])))
      .catch(() => setCities(uniq([...(d?.cities || []), value.district])));
  }, [value.district, value.state, districts, withDistrict]);

  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div className={`grid gap-3 ${withDistrict ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
      <SearchSelect options={states} value={value.state} placeholder="State" testId={`${testId}-state`}
        onChange={(v) => set({ state: v, district: '', city: '' })} />
      {withDistrict && (
        <SearchSelect options={districts.map((d) => d.district)} value={value.district} placeholder="District" disabled={!value.state}
          testId={`${testId}-district`} onChange={(v) => set({ district: v, city: '' })} />
      )}
      <SearchSelect options={cities} value={value.city} placeholder="City / Town" disabled={withDistrict ? !value.district : !value.state}
        testId={`${testId}-city`} onChange={(v) => set({ city: v })} />
    </div>
  );
};

export const MultiLocation = ({ value = [], onChange, testId }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const timer = useRef();

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) return setResults([]);
    timer.current = setTimeout(() => {
      publicApi.get('/location-master/locations/search', { params: { q: q.trim(), limit: 10 } })
        .then((r) => setResults(r.data || [])).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);

  const add = (loc) => {
    if (!value.some((v) => v.display === loc.display)) onChange([...value, { city: loc.city, district: loc.district, state: loc.state, display: loc.display }]);
    setQ(''); setResults([]);
  };

  return (
    <div className="relative">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2" data-testid={`${testId}-selected`}>
          {value.map((v) => (
            <span key={v.display} className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 rounded-lg bg-orange-50 border border-orange-200 text-sm font-medium text-slate-800">
              <MapPin className="w-3.5 h-3.5 text-[var(--jf-primary)]" />{v.display}
              <button type="button" onClick={() => onChange(value.filter((x) => x.display !== v.display))} className="hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
            </span>
          ))}
        </div>
      )}
      <input className={inputCls} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type a city or district (min 2 letters)" data-testid={testId} />
      {results.length > 0 && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl max-h-60 overflow-auto">
          {results.map((r) => (
            <button type="button" key={r.display} onClick={() => add(r)} data-testid={`${testId}-option`}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-orange-50 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-slate-400" />{r.display}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
